import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { SwitchActivity } from 'app/model/pipeline/switch.activity';
import { Metamodel } from 'app/model/base/model';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { Owned } from 'app/model/base/named';
import { ActivityAsyncAdapter } from 'app/model/adapters/activities/activity.adapter';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { ContainerActivityCase } from 'app/model/pipeline/container-activity-case';
import {
    IPipelineCopilotCurrentPipelineContext,
    ISwitchActivityInterimContext,
    ISwitchCaseInterimContext,
    PipelineActivityInterimContext
} from 'app/interfaces/copilot.pipeline.action.types';
import { Activity, arrayIsNullOrEmpty } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SwitchActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        type: '*',
        description: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            on: '*',
            cases: '*',
            defaultActivities: '*'
        }
    }
})
export class SwitchActivityAdapter extends ActivityAsyncAdapter<
    SwitchActivity,
    Model.SwitchActivity,
    ISwitchActivityInterimContext
> {
    public override async toFabricCopilotContext(
        switchActivity: SwitchActivity
    ): Promise<ISwitchActivityInterimContext> {
        if (switchActivity) {
            const switchActivityContext: ISwitchActivityInterimContext = {
                ...(await super.toFabricCopilotContext(switchActivity)),
                Expression: SwitchActivity.EXPRESSION.templateValue(switchActivity),
                DefaultChildActivities: [],
                Cases: []
            };
            for (const activity of switchActivity.defaultPipeline?.activities ?? []) {
                const adapter = this.context.asyncAdapterFor<Activity, Model.Activity, PipelineActivityInterimContext>(
                    Metamodel.modelOf(activity)
                );
                const activityContext = await adapter.toFabricCopilotContext(activity);
                if (activityContext) {
                    switchActivityContext.DefaultChildActivities.push(activityContext);
                }
            }
            for (const switchCase of switchActivity.cases ?? []) {
                const caseContext: ISwitchCaseInterimContext = {
                    ExpressionReturnValue: switchCase.value,
                    ChildActivities: []
                };

                for (const activity of switchCase.casePipeline?.activities ?? []) {
                    const adapter = this.context.asyncAdapterFor<
                        Activity,
                        Model.Activity,
                        PipelineActivityInterimContext
                    >(Metamodel.modelOf(activity));
                    const activityContext = await adapter.toFabricCopilotContext(activity);
                    if (activityContext) {
                        caseContext.ChildActivities.push(activityContext);
                    }
                }
                switchActivityContext.Cases.push(caseContext);
            }

            return switchActivityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: ISwitchActivityInterimContext,
        model?: SwitchActivity
    ): Promise<SwitchActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Expression != null) {
                SwitchActivity.EXPRESSION.setTemplateValue(model, context.Expression);
            }

            const adapter = this.context.asyncAdapterFor<
                Pipeline,
                Model.PipelineResource,
                IPipelineCopilotCurrentPipelineContext
            >(Pipeline.MODEL);
            if (!model.defaultPipeline) {
                model.defaultPipeline = Owned.create<Pipeline>(Pipeline.MODEL, model);
                model.defaultPipeline.name = model.getSwitchCasePipelineName();
            }
            if (!arrayIsNullOrEmpty(context.DefaultChildActivities)) {
                await adapter.fromFabricCopilotContext(
                    {
                        Activities: context.DefaultChildActivities
                    },
                    model.defaultPipeline
                );
            } else {
                model.defaultPipeline.activities.clear();
            }
            if (!arrayIsNullOrEmpty(context.Cases)) {
                const newCases = [];
                for (const caseContext of context.Cases) {
                    if (caseContext.ExpressionReturnValue) {
                        const containerActivityCase: ContainerActivityCase = Owned.create<ContainerActivityCase>(
                            ContainerActivityCase.MODEL,
                            model
                        );
                        containerActivityCase.value = caseContext.ExpressionReturnValue;
                        containerActivityCase.casePipeline = Owned.create<Pipeline>(
                            Pipeline.MODEL,
                            containerActivityCase
                        );
                        containerActivityCase.casePipeline.name = model.getSwitchCasePipelineName(
                            containerActivityCase.value
                        );
                        if (!arrayIsNullOrEmpty(caseContext.ChildActivities)) {
                            await adapter.fromFabricCopilotContext(
                                {
                                    Activities: caseContext.ChildActivities
                                },
                                containerActivityCase.casePipeline
                            );
                        }
                        newCases.push(containerActivityCase);
                    }
                }
                if (!arrayIsNullOrEmpty(newCases)) {
                    model.cases = newCases;
                }
            } else {
                model.cases = [];
            }

            return model;
        }
        return undefined;
    }

    public override async postToModel(activity: SwitchActivity): Promise<void> {
        await super.postToModel(activity);

        await Promise.all(
            Array.from(activity.defaultPipeline.activities.values()).map(async (innerActivity) => {
                const activityModel = Metamodel.modelOf(innerActivity);
                const adapter = this.context.isAsyncAdapter(activityModel)
                    ? this.context.asyncAdapterFor(activityModel)
                    : this.context.adapterFor(activityModel);
                try {
                    await adapter?.postToModel(innerActivity);
                } catch (e) {
                    this._logError(
                        `Activity "${innerActivity.name}" \`postToModel\` failed in switch activity of pipeline "${activity.defaultPipeline.name}"`,
                        e
                    );
                }
            })
        );

        await Promise.all(
            Array.from(activity.cases).map(async (cases) => {
                Array.from(cases.casePipeline.activities).map(async (innerActivity) => {
                    const activityModel = Metamodel.modelOf(innerActivity);
                    const adapter = this.context.isAsyncAdapter(activityModel)
                        ? this.context.asyncAdapterFor(activityModel)
                        : this.context.adapterFor(activityModel);
                    try {
                        await adapter?.postToModel(innerActivity);
                    } catch (e) {
                        this._logError(
                            `Activity "${innerActivity.name}" \`postToModel\` failed in switch activity of pipeline "${cases.casePipeline.name}"`,
                            e
                        );
                    }
                })
            })
        );
    }

    protected async _toResource(activity: SwitchActivity): Promise<Model.SwitchActivity> {
        const modelSwitchActivity: Model.SwitchActivity = super._getCommonActivityProperties(
            activity,
            'Switch'
        ) as Model.SwitchActivity;

        modelSwitchActivity.typeProperties = modelSwitchActivity.typeProperties || { on: undefined };

        if (!ValueAdapter.isTemplated(activity, SwitchActivity.EXPRESSION)) {
            SwitchActivity.EXPRESSION.setTemplateValue(activity, SwitchActivity.EXPRESSION.value(activity));
        }

        modelSwitchActivity.typeProperties.on = ValueAdapter.toResource(activity, SwitchActivity.EXPRESSION);
        modelSwitchActivity.typeProperties.cases = [];

        const adapter = this.context.asyncAdapterFor(Pipeline.MODEL);

        if (adapter) {
            const cases = activity.cases.filter((switchCase: ContainerActivityCase) => switchCase !== null);
            await Promise.all(
                cases.map(async (switchCase: ContainerActivityCase) => {
                    const casePipeline = await adapter.toResource(switchCase.casePipeline);
                    const modelSwitchCase: Model.SwitchCase = {
                        value: switchCase.value,
                        activities: casePipeline.properties ? casePipeline.properties.activities : []
                    };
                    return modelSwitchCase;
                })
            ).then((modelCases: Model.SwitchCase[]) => {
                modelSwitchActivity.typeProperties.cases = modelCases;
            });

            const defaultPipeline = await adapter.toResource(activity.defaultPipeline);
            if (defaultPipeline.properties) {
                modelSwitchActivity.typeProperties.defaultActivities = defaultPipeline.properties.activities;
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelSwitchActivity, parsed);
        }

        return modelSwitchActivity;
    }

    protected async _toModel(modelActivity: Model.SwitchActivity): Promise<SwitchActivity> {
        const activity: SwitchActivity = SwitchActivity.MODEL.create<SwitchActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);
        modelActivity.typeProperties = modelActivity.typeProperties || { on: undefined };
        ValueAdapter.toModel(activity, SwitchActivity.EXPRESSION, modelActivity.typeProperties.on);

        const adapter = this.context.asyncAdapterFor(Pipeline.MODEL);

        if (adapter) {
            const tempCases: Model.SwitchCase[] = modelActivity.typeProperties.cases;
            await Promise.all(
                tempCases.map(async (switchCase: Model.SwitchCase) => {
                    const tempCasePipeline: Model.PipelineResource = {
                        properties: { activities: switchCase.activities }
                    };
                    const containerActivityCase: ContainerActivityCase = Owned.create<ContainerActivityCase>(
                        ContainerActivityCase.MODEL,
                        activity
                    );
                    const activityPipeline: Pipeline = Owned.create<Pipeline>(Pipeline.MODEL, containerActivityCase);
                    await adapter.toModel(tempCasePipeline, activityPipeline);
                    activityPipeline.name = activity.getSwitchCasePipelineName(switchCase.value);
                    containerActivityCase.value = switchCase.value;
                    containerActivityCase.casePipeline = activityPipeline;
                    return containerActivityCase;
                })
            ).then((switchCases: ContainerActivityCase[]) => {
                activity.cases = switchCases.filter((switchCase: ContainerActivityCase) => switchCase !== null);
            });

            const tempDefaultPipeline: Model.PipelineResource = {
                properties: { activities: modelActivity.typeProperties.defaultActivities }
            };
            activity.defaultPipeline = Owned.create<Pipeline>(Pipeline.MODEL, activity);
            await adapter.toModel(tempDefaultPipeline, activity.defaultPipeline);
            activity.defaultPipeline.name = activity.getSwitchCasePipelineName();
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, SwitchActivity.MODEL));
        return activity;
    }
}
